import { FunctionComponent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Card, CardTitle } from '../../../components';
import SignInButtons from '../SignInButtons/SignInButtons';
import SignInForm from '../SignInForm/SignInForm';
import useNavigateWithSearchParams from '../../../behaviors/use-navigate-with-search-params/use-navigate-with-search-params';
import { useSettings } from '../../../providers/SettingsProvider/SettingsProvider';
import { ECTEACHER_APP } from '../../../utils/constants';
import { ReactComponent as TeacherIcon } from '../../../assets/icons/teacher.svg';
import { ReactComponent as StudentIcon } from '../../../assets/icons/student.svg';
import useClasses from '../../../behaviors/use-classes/use-classes';
import { useDpState } from '../../../providers/DpStateProvider/DpStateProvider';
import useOnMount from '../../../behaviors/use-on-mount/use-on-mount';
import { useWindowQuery } from '../../../providers/ResponsivityProvider/ResponsivityProvider';

import './SignInStudent.scss';

const baseClass = `customer-portal-sign-in--student`;

const CardHeader = () => {
  return (
    <div className={`${baseClass}__header`}>
      <StudentIcon className={`${baseClass}__student-icon`} />
      <CardTitle className="title">Student Sign In</CardTitle>
    </div>
  );
};

const AltSignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return (
    <div className={`${baseClass}__alt-sign-in`}>
      <TeacherIcon className={`${baseClass}__teacher-icon`} />
      <p>
        Not a student?
        <Button
          data-testid="signInFormToggle"
          color="default"
          link
          onClick={() => {
            searchParams.delete('role');
            navigate({ pathname: '/sign-in/staff', search: searchParams.toString() });
          }}
        >
          Sign in as an educator or librarian
        </Button>
      </p>
    </div>
  );
};

export const SignInStudent: FunctionComponent = () => {
  const { isExtraLarge, responsiveClassName } = useWindowQuery({ className: baseClass });
  const classes = useClasses({ baseClass }, [responsiveClassName]);

  const { googleLoginEnabled, cleverLoginEnabled } = useSettings();
  const { dispatch } = useDpState();

  const { navigateWithSearchParams } = useNavigateWithSearchParams();
  const [searchParams] = useSearchParams();

  useOnMount(() => {
    dispatch({ type: 'role', value: 'student' });
  });

  if (
    searchParams.get('role') === 'staff' ||
    searchParams.get('acode') ||
    searchParams.get('app') === ECTEACHER_APP
  ) {
    navigateWithSearchParams('/sign-in/staff');
    return null;
  }

  return (
    <div className={classes}>
      {!isExtraLarge && <AltSignIn />}
      <Card tag="h1" className={`${baseClass}__card`}>
        {isExtraLarge && <CardHeader />}
        <div className={`${baseClass}__content`}>
          <SignInForm />
          <div className={`${baseClass}__buttons`}>
            <p>Or, sign in with:</p>
            <SignInButtons showGoogle={!!googleLoginEnabled} showClever={!!cleverLoginEnabled} />
          </div>
        </div>
      </Card>
      {isExtraLarge && <AltSignIn />}
    </div>
  );
};

export default SignInStudent;
